/**
 * CreateVariantDialog.jsx — Extracted from Inventory.jsx (iter-120 refactor)
 * to decouple the Create-Variant form from the rest of the inventory page.
 * Same "dumb" pattern as CreateSkuDialog — no API state, no fetches,
 * delegates everything via props.
 *
 * Props:
 *  - open               boolean
 *  - onOpenChange       (boolean) => void
 *  - newVariant         form-state object
 *  - setNewVariant      setter for form state
 *  - skus               list to populate the parent-SKU dropdown
 *  - onSubmit           form submit handler
 *  - onScanBarcode      () => void — opens scanner with target='newVariant'
 */
import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Plus, Camera } from '@phosphor-icons/react';

export default function CreateVariantDialog({
  open, onOpenChange, newVariant, setNewVariant, skus, onSubmit, onScanBarcode,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="create-variant-btn">
          <Plus size={18} className="mr-2" /> New Variant
        </Button>
      </DialogTrigger>
      <DialogContent className="border border-border rounded-none">
        <DialogHeader>
          <DialogTitle>Create New Variant</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label className="overline">Parent SKU</Label>
            <Select value={newVariant.sku_id} onValueChange={(v) => setNewVariant({ ...newVariant, sku_id: v })}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Select SKU" /></SelectTrigger>
              <SelectContent>
                {skus.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.sku_code} - {s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="overline">Size</Label>
              <Input value={newVariant.size} onChange={(e) => setNewVariant({ ...newVariant, size: e.target.value })} className="mt-1" placeholder="S, M, L" />
            </div>
            <div>
              <Label className="overline">Color</Label>
              <Input value={newVariant.color} onChange={(e) => setNewVariant({ ...newVariant, color: e.target.value })} className="mt-1" placeholder="Red" />
            </div>
            <div>
              <Label className="overline">Style</Label>
              <Input value={newVariant.style} onChange={(e) => setNewVariant({ ...newVariant, style: e.target.value })} className="mt-1" placeholder="Classic" />
            </div>
          </div>
          <div>
            <Label className="overline flex items-center justify-between">
              <span>Barcode</span>
              <span className="text-[10px] font-normal normal-case tracking-normal text-muted-foreground">optional</span>
            </Label>
            <div className="mt-1 flex gap-1">
              <Input
                value={newVariant.barcode}
                onChange={(e) => setNewVariant({ ...newVariant, barcode: e.target.value })}
                className="font-mono flex-1"
                placeholder="Manufacturer UPC / EAN / GTIN"
                maxLength={64}
                data-testid="new-variant-barcode"
              />
              <Button
                type="button"
                variant="outline"
                onClick={onScanBarcode}
                data-testid="new-variant-barcode-scan"
                title="Scan barcode with camera"
                className="shrink-0 px-3"
                aria-label="Scan variant barcode"
              >
                <Camera size={16} weight="bold" />
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">Leave blank to auto-generate · scan or type existing barcode</p>
          </div>
          <Button type="submit" className="w-full bg-brand hover:bg-brand/90">Create Variant</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
